import { lazy, memo, Suspense, useCallback } from "react";

import {
  Box,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  InfoOutlined,
} from "@mui/icons-material";
import PropTypes from "prop-types";

import { HyperLink } from "components";

import FGHoverCard from "./FGHoverCard";
import FGSellerInfoTooltip from "./FGSellerInfoTooltip";
import FGStatusChip from "./FGStatusChip";
import { FG_TRACKING_LABELS } from "../constants";
import {
  formatDeltaLabel,
  getDaysFromDelta,
  getFormattedAgeing,
} from "../makeData";

import { validateNull } from "utils";

const FGLineItemTable = lazy(() => import("./FGLineItemTable"));
const FGOrderProcessingLineItemTable = lazy(() =>
  import("./FGOrderProcessingLineItemTable"),
);

const orderProcessingTabs = ["paymentPending", "doPending", "doCreated"];

const getPaymentTone = (paymentStatus) => {
  if (paymentStatus === FG_TRACKING_LABELS.partialPayment) {
    return "warning";
  }
  if (paymentStatus === FG_TRACKING_LABELS.pendingPayment) {
    return "danger";
  }
  return "success";
};

const FGTrackingTableRow = ({
  order,
  isExpanded,
  onToggleExpand,
  selectedLineItemIds,
  // onToggleLineItem,
  onToggleSelectAll,
  columnLength,
}) => {
  const theme = useTheme();
  const hasTimeline = order.timeline?.length > 0;
  const hasSellerInfo = Boolean(order.sellerInfo);
  const fgReadyByTone = order.fgReadyByDelta?.includes("+")
    ? "success"
    : "danger";
  const fgReadyByDeltaDays = getDaysFromDelta(order.fgReadyByDelta);
  const isOrderProcessingTab = orderProcessingTabs.includes(order.tab);

  const handleExpandToggle = useCallback(() => {
    onToggleExpand(order.orderId);
  }, [onToggleExpand, order.orderId]);

  const sourceSellerName =
    order.sellerInfo?.sourceSeller?.sellerDisplayName ||
    order.sellerInfo?.sourceSeller?.name ||
    order.sellerName;

  const doIds = [
    ...new Set(
      (order?.lineItems ?? [])
        .map((lineItem) => lineItem?.doId)
        .filter(Boolean),
    ),
  ].join(", ");

  return (
    <>
      <TableRow hover>
        <TableCell width={40}>
          <IconButton size="small" onClick={handleExpandToggle}>
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ display: "flex", flexDirection: "column" }}>
          <HyperLink title={order.buyerOrderNo} fontWeight={600} />
          <Typography variant="caption" color="text.secondary">
            {order.tab !== "doCreated" ? order.doId : doIds}
          </Typography>
        </TableCell>
        <TableCell>
          {hasSellerInfo ? (
            <FGSellerInfoTooltip sellerInfo={order.sellerInfo}>
              <Typography
                variant="body2"
                color="primary.main"
                fontWeight={600}
                sx={{ textDecoration: "underline", cursor: "pointer" }}
              >
                {validateNull(sourceSellerName)}
              </Typography>
            </FGSellerInfoTooltip>
          ) : (
            <Typography variant="body2" color="primary.main" fontWeight={600}>
              {validateNull(sourceSellerName)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {order.sellerOrder}
          </Typography>
        </TableCell>
        <TableCell>
          {hasTimeline ? (
            <FGHoverCard title="Order Timeline" timeline={order.timeline}>
              <Typography
                variant="body2"
                sx={{
                  textDecorationLine: "underline",
                  textDecorationStyle: "dotted",
                  cursor: "pointer",
                  textUnderlineOffset: "2px",
                }}
              >
                {order.expectedDispatchDate}
              </Typography>
            </FGHoverCard>
          ) : (
            order.expectedDispatchDate
          )}
        </TableCell>
        <TableCell>{order.buyer}</TableCell>
        <TableCell>{order.shipTo}</TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{order.totalQty}</TableCell>
        <TableCell sx={{ minWidth: 126 }}>
          <FGStatusChip label={order.fgReady} tone={order.fgReadyTone} />
        </TableCell>
        <TableCell>
          <FGStatusChip
            label={order.payment}
            tone={getPaymentTone(order.payment)}
          />
        </TableCell>
        <TableCell>
          {!order.fgReadyBy || order.fgReadyBy === "-" ? (
            <Typography variant="body2" fontWeight={700} color="text.secondary">
              -
            </Typography>
          ) : (
            <>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    color:
                      fgReadyByTone === "danger"
                        ? theme.palette.error.dark
                        : theme.palette.green.dark,
                    mb: 0.5,
                    mr: 1,
                  }}
                >
                  {formatDeltaLabel(order.fgReadyByDelta)}
                </Typography>
                {fgReadyByDeltaDays < 0 && (
                  <Tooltip
                    title={`FG ready date passed ${Math.abs(
                      fgReadyByDeltaDays || 0,
                    )} days ago`}
                  >
                    <Box
                      component="span"
                      sx={{ color: theme.palette.error.dark }}
                    >
                      <InfoOutlined fontSize="small" />
                    </Box>
                  </Tooltip>
                )}
              </Box>

              <FGStatusChip label={order.fgReadyBy} tone={fgReadyByTone} />
            </>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={700}>
            {getFormattedAgeing(order.ageing)}
          </Typography>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={columnLength}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box py={1.5}>
              {order.lineItems.length > 0 ? (
                <Suspense fallback={null}>
                  {isOrderProcessingTab ? (
                    <FGOrderProcessingLineItemTable order={order} />
                  ) : (
                    <FGLineItemTable
                      order={order}
                      selectedLineItemIds={selectedLineItemIds}
                      onToggleSelectAll={onToggleSelectAll}
                    />
                  )}
                </Suspense>
              ) : (
                <Box
                  p={2}
                  border="1px solid"
                  borderColor="grey.300"
                  borderRadius={1.5}
                >
                  <Typography variant="body2" color="text.secondary">
                    {FG_TRACKING_LABELS.noLineItems}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

FGTrackingTableRow.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    sellerName: PropTypes.string.isRequired,
    sellerOrder: PropTypes.string.isRequired,
    expectedDispatchDate: PropTypes.string.isRequired,
    buyer: PropTypes.string.isRequired,
    shipTo: PropTypes.string.isRequired,
    totalQty: PropTypes.string.isRequired,
    fgReady: PropTypes.string.isRequired,
    payment: PropTypes.string.isRequired,
    fgReadyBy: PropTypes.string.isRequired,
    fgReadyByDelta: PropTypes.string,
    ageing: PropTypes.string.isRequired,
    timeline: PropTypes.array,
    sellerInfo: PropTypes.object,
    lineItems: PropTypes.array.isRequired,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  selectedLineItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleSelectAll: PropTypes.func.isRequired,
  columnLength: PropTypes.number.isRequired,
};

export default memo(FGTrackingTableRow);